import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, RefreshCw } from "lucide-react";
import { getUnsavedCount } from "@/hooks/useUnsavedChanges";

/**
 * Listens for `pwa:update-available` events from register-sw.ts.
 * Shows a persistent toast; on "Update now" it warns if the doctor has
 * unsaved forms open before applying the waiting service worker.
 */
export default function PwaUpdatePrompt() {
  const [available, setAvailable] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    const onAvail = () => {
      if (available) return;
      setAvailable(true);
      toast("A new version of MediDesk is ready", {
        id: "pwa-update",
        description: "নতুন সংস্করণ প্রস্তুত — এখনই আপডেট করুন।",
        duration: Infinity,
        icon: <Download className="h-4 w-4" />,
        action: {
          label: "Update now",
          onClick: () => tryApply(),
        },
      });
    };
    window.addEventListener("pwa:update-available", onAvail);
    return () => window.removeEventListener("pwa:update-available", onAvail);
  }, [available]);

  const tryApply = () => {
    if (getUnsavedCount() > 0) {
      setConfirmOpen(true);
      return;
    }
    applyNow();
  };

  const applyNow = async () => {
    toast.loading("Updating…", { id: "pwa-update" });
    try {
      await window.__pwaApplyUpdate?.();
    } catch {
      toast.error("Update failed. Please refresh manually.", { id: "pwa-update" });
    }
  };

  return (
    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-primary" />
            Unsaved changes detected
          </AlertDialogTitle>
          <AlertDialogDescription>
            You have {getUnsavedCount()} unsaved form
            {getUnsavedCount() === 1 ? "" : "s"} open (e.g. a prescription or patient
            edit). Updating now will reload the app and{" "}
            <strong>your unsaved work will be lost</strong>.
            <br />
            <span className="mt-2 block text-muted-foreground">
              সংরক্ষণ না-করা পরিবর্তন হারিয়ে যাবে। আগে সেভ করে নিন।
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Save first</AlertDialogCancel>
          <Button variant="destructive" asChild>
            <AlertDialogAction onClick={applyNow}>
              Discard & update
            </AlertDialogAction>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
